import { useEffect, useRef, useState } from "react";
import { editGroundRow, rowFootprint, rowDirection } from "../utils/groundRowEditing";
import { alignGroundRow } from "../utils/groundRowAlignment";

// All coordinates are shared with the 3D scene; only SVG's north/y is inverted.
export default function GroundRowsLayer({ layout, depth, moduleWidth, tiers, azimuth, pitch, setback,
  onChange, fromEvent, scale, greek, onError, selected, onSelect, moveGroup = false }) {
  const drag = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [guide, setGuide] = useState(null);
  const tr = (el, en) => greek ? el : en;
  const path = points => points.map(p => `${p.x},${-p.y}`).join(" ");
  useEffect(() => {
    if (!dragging) return undefined;
    const cancel = event => {
      if (event.key !== "Escape") return;
      event.preventDefault(); event.stopPropagation();
      if (drag.current) onChange(drag.current.before);
      drag.current = null; setDragging(false); setGuide(null); onError("");
    };
    document.addEventListener("keydown", cancel, true);
    return () => document.removeEventListener("keydown", cancel, true);
  }, [dragging, onChange, onError]);
  if (!layout) return null;
  const u = rowDirection(azimuth), c = { x: u.y, y: -u.x };
  const centre = t => ({ x: (t.start[0] + t.end[0]) / 2, y: (t.start[1] + t.end[1]) / 2 });
  const picked = layout.tables[selected];
  const p = picked && centre(picked);
  const neighbours = p ? [-1, 1].map(sign => layout.tables.map((t, i) => {
    const q = centre(t), across = (q.x - p.x) * c.x + (q.y - p.y) * c.y;
    return { i, across, distance: Math.abs(across) };
  }).filter(n => n.i !== selected && sign * n.across > 0.01).sort((a, b) => a.distance - b.distance)[0]).filter(Boolean) : [];
  return <g data-testid="ground-rows-layer"
    onPointerDown={event => {
      if (event.button !== 0) return;
      const row = event.target.closest?.("[data-ground-row]");
      if (!row || !onChange) return;
      event.preventDefault(); event.stopPropagation();
      const index = Number(row.getAttribute("data-ground-row"));
      const end = moveGroup ? null : event.target.getAttribute("data-row-end");
      onSelect(index); onError(""); setGuide(null);
      drag.current = { before: layout, index, end, all: moveGroup, start: fromEvent(event) };
      setDragging(true); event.currentTarget.setPointerCapture?.(event.pointerId);
    }}
    onPointerMove={event => {
      const d = drag.current;
      if (!d) return;
      event.preventDefault(); event.stopPropagation();
      const q = fromEvent(event), t = d.before.tables[d.index];
      const delta = { x: q.x - d.start.x, y: q.y - d.start.y };
      const alignment = d.end || d.all ? { delta, guide: null }
        : alignGroundRow(d.before.tables, d.index, delta, azimuth, scale, event.altKey);
      const operation = d.end ? { type: "resize", index: d.index, end: d.end,
        columns: t.columns + (d.end === "start" ? -1 : 1) * (delta.x * u.x + delta.y * u.y) / moduleWidth }
        : { type: d.all ? "move-group" : "move", index: d.index, delta: alignment.delta };
      let next = editGroundRow(d.before, operation, { moduleWidth, depth, setback, azimuth, pitch });
      // Assistance must never block a valid free position at a parcel boundary.
      if (!next && alignment.guide?.snapped) {
        next = editGroundRow(d.before, { type: "move", index: d.index, delta }, { moduleWidth, depth, setback, azimuth, pitch });
        alignment.guide = alignGroundRow(d.before.tables, d.index, delta, azimuth, scale, true).guide;
      }
      if (next) { onChange(next); setGuide(alignment.guide); onError(""); }
      else { setGuide(null); onError(d.all
        ? tr("Όλες οι σειρές της ομάδας πρέπει να παραμένουν μέσα στο γεωτεμάχιο και στην απόσταση ασφαλείας που ορίσατε.",
          "Keep every row of the group inside the parcel boundary and setback.")
        : tr("Η σειρά πρέπει να μένει μέσα στα όρια, με την απόσταση ασφαλείας που ορίσατε, και να μην επικαλύπτει άλλη σειρά.",
          "Keep the row inside the boundary and setback, without overlapping another row.")); }
    }}
    onPointerUp={event => { if (drag.current) event.stopPropagation(); drag.current = null; setDragging(false); setGuide(null); }}
    onPointerCancel={event => {
      if (drag.current) { event.stopPropagation(); onChange(drag.current.before); }
      drag.current = null; setDragging(false); setGuide(null);
    }}
    onLostPointerCapture={() => { drag.current = null; setDragging(false); setGuide(null); }}
    onClick={event => event.stopPropagation()}>
    {layout.tables.map((t, i) => {
      const quad = rowFootprint(t, depth), mid = centre(t), active = moveGroup || i === selected;
      return <g key={i} data-ground-row={i} style={{ cursor: "move" }}>
        <polygon points={path(quad)} fill={active ? "#3b82c4" : "#5892b5"} fillOpacity="0.92"
          stroke={active ? "#f59e0b" : "#e2effa"} strokeWidth={scale * (active ? 2.5 : 1)} />
        {Array.from({ length: Math.max(0, t.columns - 1) }, (_, k) => {
          const f = (k + 1) / t.columns;
          return <line key={k} x1={quad[0].x + (quad[1].x - quad[0].x) * f} y1={-(quad[0].y + (quad[1].y - quad[0].y) * f)}
            x2={quad[3].x + (quad[2].x - quad[3].x) * f} y2={-(quad[3].y + (quad[2].y - quad[3].y) * f)}
            stroke="#d5e7ef" strokeWidth={scale * 0.6} pointerEvents="none" />;
        })}
        {Array.from({ length: Math.max(0, tiers - 1) }, (_, k) => {
          const f = (k + 1) / tiers;
          return <line key={k} x1={quad[0].x + (quad[3].x - quad[0].x) * f} y1={-(quad[0].y + (quad[3].y - quad[0].y) * f)}
            x2={quad[1].x + (quad[2].x - quad[1].x) * f} y2={-(quad[1].y + (quad[2].y - quad[1].y) * f)}
            stroke="#d5e7ef" strokeWidth={scale * 0.6} pointerEvents="none" />;
        })}
        <text x={mid.x} y={-mid.y} textAnchor="middle" dominantBaseline="central" fontSize={scale * 12}
          fill="#fff" stroke="#244564" strokeWidth={scale * 3} paintOrder="stroke" pointerEvents="none">{t.label}</text>
        {active && !moveGroup && ["start", "end"].map(end => <circle key={end} data-row-end={end}
          cx={t[end][0]} cy={-t[end][1]} r={scale * 7} fill="#fff" stroke="#d97706" strokeWidth={scale * 2}
          style={{ cursor: "ew-resize" }}><title>{tr("Σύρετε για αλλαγή μήκους", "Drag to resize row")}</title></circle>)}
      </g>;
    })}
    {guide && <g pointerEvents="none" data-testid="row-alignment-guide">
      <line x1={guide.target.x} y1={-guide.target.y} x2={guide.projected.x} y2={-guide.projected.y}
        stroke={guide.snapped ? "#047857" : "#f59e0b"} strokeWidth={scale * 2} strokeDasharray={`${scale * 6} ${scale * 4}`} />
      <line x1={guide.source.x} y1={-guide.source.y} x2={guide.projected.x} y2={-guide.projected.y}
        stroke="#172f4b" strokeWidth={scale * 1.5} />
      <text x={guide.source.x + scale * 10} y={-guide.source.y - scale * 12} fontSize={scale * 12}
        fill={guide.snapped ? "#047857" : "#172f4b"} stroke="white" strokeWidth={scale * 3} paintOrder="stroke">
        {`${guide.distance.toLocaleString(greek ? "el-GR" : "en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m · ${guide.label || ""} · `}
        {guide.snapped ? tr("ευθυγραμμισμένη", "aligned") : tr("από την ευθεία της ακμής", "from edge alignment")}
      </text>
    </g>}
    {neighbours.map(n => {
      const q = { x: p.x + c.x * n.across, y: p.y + c.y * n.across };
      return <g key={n.i} pointerEvents="none">
        <line x1={p.x} y1={-p.y} x2={q.x} y2={-q.y} stroke="#172f4b" strokeDasharray={`${scale * 5} ${scale * 4}`} strokeWidth={scale * 1.5} />
        <text x={(p.x + q.x) / 2 + scale * 6} y={-(p.y + q.y) / 2} fontSize={scale * 12}
          fill="#172f4b" stroke="white" strokeWidth={scale * 3} paintOrder="stroke">{n.distance.toFixed(2)} m</text>
      </g>;
    })}
  </g>;
}
